import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { StarredQuestion } from '../hooks/useStarredQuestions';

interface SavedQuestionsScreenProps {
  isAuthenticated: boolean;
  questions: StarredQuestion[];
  onRemove: (id: string) => void;
  onBack: () => void;
}

const SavedQuestionsScreen: React.FC<SavedQuestionsScreenProps> = ({
  isAuthenticated,
  questions,
  onRemove,
  onBack,
}) => {
  const navigate = useNavigate();

  return (
    <div
      className="relative min-h-screen flex items-center justify-center lg:justify-end bg-cover bg-center overflow-hidden px-4 lg:pr-4 lg:pl-28"
      style={{ backgroundImage: "url('/backgrounds/game-landing-bg.svg')" }}
    >
      <div className="mx-auto max-w-4xl h-[calc(100vh-120px)] rounded-2xl border border-[#2E3A6B] bg-[#1b2a41]/90 p-6 sm:p-8 shadow-2xl flex flex-col font-caudex">
        <div className="flex items-center justify-between mb-6 gap-3">
          <h1
            style={{
              color: '#A5FFF5',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 'clamp(26px, 3vw, 36px)',
              fontWeight: 400,
            }}
          >
            Saved Questions
          </h1>
          <button
            onClick={onBack}
            className="px-4 py-2 rounded-full border border-white/30 hover:bg-white/10 transition text-white text-sm font-semibold font-caudex"
          >
            Back
          </button>
        </div>
        {!isAuthenticated ? (
          <div className="rounded-xl border border-white/15 bg-[#0d1b3e]/70 p-6 text-center">
            <p className="text-white/90 mb-4">
              Create an account or log in to save and review starred questions.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => navigate('/register')}
                className="px-5 py-2 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex"
              >
                Register
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2 rounded-full border border-white/40 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
              >
                Log In
              </button>
            </div>
          </div>
        ) : questions.length === 0 ? (
          <div className="rounded-xl border border-white/15 bg-[#0d1b3e]/70 p-6 text-center text-white/80">
            You have not starred any questions yet.
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto pr-1 flex-1 min-h-0">
            {questions.map((item) => (
              <article
                key={item.id}
                className="rounded-xl border border-white/15 bg-[#0d1b3e]/75 p-4 sm:p-5"
              >
                <p className="text-sm text-cyan-200 mb-2">
                  {item.sdgNumbers.length === 1
                    ? `SDG ${item.sdgNumbers[0]}`
                    : item.sdgNumbers.length > 1
                      ? `SDGs ${item.sdgNumbers.join(', ')}`
                      : 'SDG -'}
                </p>
                <p className="text-white text-base sm:text-lg mb-3">{item.questionText}</p>
                <p className="text-white/90 text-sm sm:text-base mb-4">
                  <span className="text-cyan-200">Correct answer:</span> {item.correctAnswer}
                </p>
                <p className="text-white/90 text-sm sm:text-base mb-4">
                  <span className="text-cyan-200">Your answer:</span> {item.userAnswer ?? 'Not answered yet'}
                </p>
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={() => onRemove(item.id)}
                    className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 transition text-sm font-caudex"
                  >
                    Remove
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedQuestionsScreen;

